import React, {useState, useEffect} from 'react';
import {
    ClipboardList,
    Loader2,
    AlertTriangle,
    RefreshCw,
    Search,
    Mail,
    User,
    Calendar,
    Building,
    ChevronLeft,
    ChevronRight,
    Filter,
    CheckCircle2,
    XCircle,
    Clock,
    ChevronDown,
    ChevronUp,
    BookOpen,
    Phone,
    BarChart2,
    Clock3,
    FileText,
    CheckSquare,
    BadgeX,
    LayoutGrid,
    LayoutList
} from 'lucide-react';

// Extracted StatusChangeModal as a separate component to fix focus issue
const StatusChangeModal = ({
                               request,
                               adminNote,
                               setAdminNote,
                               isUpdating,
                               onClose,
                               onUpdateStatus,
                               getStatusColors
                           }) => {
    if (!request) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    Update Request Status
                </h3>

                <div className="space-y-4 mb-6">
                    <div>
                        <p className="text-sm font-medium text-gray-500">User</p>
                        <p className="font-medium">{request.user_name} ({request.user_email})</p>
                    </div>

                    <div>
                        <p className="text-sm font-medium text-gray-500">Research Topic</p>
                        <p>{request.research_topic}</p>
                    </div>

                    <div>
                        <p className="text-sm font-medium text-gray-500">Current Status</p>
                        <div className="flex items-center mt-1">
                            <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColors(request.status).bg} ${getStatusColors(request.status).text}`}>
                                {getStatusColors(request.status).icon}
                                <span className="ml-1 capitalize">{request.status}</span>
                            </span>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Admin Note
                        </label>
                        <textarea
                            value={adminNote}
                            onChange={(e) => setAdminNote(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                            rows={4}
                            placeholder="Add a note about this decision (optional)"
                        />
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:justify-end gap-3 pt-4 border-t border-gray-200">
                    <button
                        onClick={onClose}
                        className="inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Cancel
                    </button>

                    <button
                        onClick={() => onUpdateStatus(request.id, 'rejected')}
                        disabled={isUpdating}
                        className="inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isUpdating ? <Loader2 size={16} className="animate-spin mr-2"/> :
                            <BadgeX size={16} className="mr-2"/>}
                        Reject Request
                    </button>

                    <button
                        onClick={() => onUpdateStatus(request.id, 'approved')}
                        disabled={isUpdating}
                        className="inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isUpdating ? <Loader2 size={16} className="animate-spin mr-2"/> :
                            <CheckSquare size={16} className="mr-2"/>}
                        Approve Request
                    </button>
                </div>
            </div>
        </div>
    );
};

const ResearchRequestsPage = () => {
    const [requests, setRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [expandedRequest, setExpandedRequest] = useState(null);
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'

    // For admin notes
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [adminNote, setAdminNote] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
        fetchRequests();
    }, [page, pageSize, statusFilter]);

    const fetchRequests = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const accessToken = localStorage.getItem('accessToken');

            let url = `https://api.digitalaalim.com/admin/researcher/requests?skip=${(page - 1) * pageSize}&limit=${pageSize}`;

            if (statusFilter) {
                url += `&status_=${statusFilter}`;
            }

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch requests: ${response.statusText}`);
            }

            const data = await response.json();

            setRequests(data.items || []);
        } catch (error) {
            console.error('Error fetching research requests:', error);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const updateRequestStatus = async (id, status) => {
        setIsUpdating(true);

        try {
            const accessToken = localStorage.getItem('accessToken');

            const response = await fetch(`https://api.digitalaalim.com/admin/researcher/requests/status`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id,
                    status,
                    admin_note: adminNote
                }),
            });

            if (!response.ok) {
                throw new Error(`Failed to update request status: ${response.statusText}`);
            }

            const updatedRequest = await response.json();

            // Update the requests array with the updated request
            setRequests(prevRequests =>
                prevRequests.map(req => req.id === id ? updatedRequest : req)
            );

            setSelectedRequest(null);
            setAdminNote('');

        } catch (error) {
            console.error('Error updating request status:', error);
            alert(`Failed to update request status: ${error.message}`);
        } finally {
            setIsUpdating(false);
        }
    };

    const toggleExpandRequest = (id) => {
        if (expandedRequest === id) {
            setExpandedRequest(null);
        } else {
            setExpandedRequest(id);
        }
    };

    const handleCloseModal = () => {
        setSelectedRequest(null);
        setAdminNote('');
    };

    // Filter requests by search term
    const filteredRequests = requests.filter(request => {
        return (
            request.user_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            request.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            request.research_topic.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (request.organization && request.organization.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (request.use_case && request.use_case.toLowerCase().includes(searchTerm.toLowerCase()))
        );
    });

    // Status color mapping
    const getStatusColors = (status) => {
        switch (status) {
            case 'pending':
                return {
                    bg: 'bg-amber-100',
                    text: 'text-amber-800',
                    icon: <Clock className="h-4 w-4 text-amber-600"/>
                };
            case 'approved':
                return {
                    bg: 'bg-emerald-100',
                    text: 'text-emerald-800',
                    icon: <CheckCircle2 className="h-4 w-4 text-emerald-600"/>
                };
            case 'rejected':
                return {
                    bg: 'bg-red-100',
                    text: 'text-red-800',
                    icon: <XCircle className="h-4 w-4 text-red-600"/>
                };
            default:
                return {
                    bg: 'bg-gray-100',
                    text: 'text-gray-800',
                    icon: <AlertTriangle className="h-4 w-4 text-gray-600"/>
                };
        }
    };

    // Format date
    const formatDate = (dateString) => {
        const options = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        };
        return new Date(dateString).toLocaleString(undefined, options);
    };

    // Render list view
    const renderListView = () => {
        return (
            <div className="space-y-4">
                {filteredRequests.length > 0 ? (
                    filteredRequests.map((request) => (
                        <div
                            key={request.id}
                            className={`bg-white rounded-xl shadow-sm overflow-hidden border-l-4 ${
                                request.status === 'pending' ? 'border-amber-400' :
                                    request.status === 'approved' ? 'border-emerald-400' : 'border-red-400'
                            } transition-all duration-200 hover:shadow-md`}
                        >
                            <div
                                className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                                onClick={() => toggleExpandRequest(request.id)}
                            >
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-start gap-4">
                                            <div
                                                className="flex-shrink-0 h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                                                <User size={24} className="text-blue-600"/>
                                            </div>

                                            <div className="flex-1 min-w-0">
                                                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                                    <h3 className="text-lg font-medium text-gray-900 truncate">{request.user_name}</h3>
                                                    <span
                                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColors(request.status).bg} ${getStatusColors(request.status).text}`}>
                            {getStatusColors(request.status).icon}
                                                        <span className="ml-1 capitalize">{request.status}</span>
                          </span>
                                                </div>

                                                <div
                                                    className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-sm text-gray-500 mt-1">
                                                    <div className="flex items-center">
                                                        <Mail size={14} className="mr-1"/>
                                                        {request.user_email}
                                                    </div>
                                                    <div className="flex items-center">
                                                        <Calendar size={14} className="mr-1"/>
                                                        {formatDate(request.created_at)}
                                                    </div>
                                                </div>

                                                <div className="mt-2">
                                                    <div
                                                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700">
                                                        <BookOpen size={14} className="mr-1.5"/>
                                                        {request.research_topic}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {expandedRequest === request.id && (
                                            <div className="mt-6 border-t border-gray-100 pt-4">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <h4 className="text-sm font-medium text-gray-500 flex items-center">
                                                            <FileText size={14} className="mr-1.5"/>
                                                            Use Case
                                                        </h4>
                                                        <p className="mt-1 text-gray-700">{request.use_case}</p>
                                                    </div>

                                                    {request.organization && (
                                                        <div>
                                                            <h4 className="text-sm font-medium text-gray-500 flex items-center">
                                                                <Building size={14} className="mr-1.5"/>
                                                                Organization
                                                            </h4>
                                                            <p className="mt-1 text-gray-700">{request.organization}</p>
                                                        </div>
                                                    )}

                                                    {request.expected_usage && (
                                                        <div>
                                                            <h4 className="text-sm font-medium text-gray-500 flex items-center">
                                                                <BarChart2 size={14} className="mr-1.5"/>
                                                                Expected Usage
                                                            </h4>
                                                            <p className="mt-1 text-gray-700 capitalize">{request.expected_usage}</p>
                                                        </div>
                                                    )}

                                                    {request.timeframe && (
                                                        <div>
                                                            <h4 className="text-sm font-medium text-gray-500 flex items-center">
                                                                <Clock3 size={14} className="mr-1.5"/>
                                                                Timeframe
                                                            </h4>
                                                            <p className="mt-1 text-gray-700">{request.timeframe}</p>
                                                        </div>
                                                    )}

                                                    {request.phone_number && (
                                                        <div>
                                                            <h4 className="text-sm font-medium text-gray-500 flex items-center">
                                                                <Phone size={14} className="mr-1.5"/>
                                                                Phone Number
                                                            </h4>
                                                            <p className="mt-1 text-gray-700">{request.phone_number}</p>
                                                        </div>
                                                    )}
                                                </div>

                                                {request.description && (
                                                    <div className="mt-4">
                                                        <h4 className="text-sm font-medium text-gray-500 mb-1 flex items-center">
                                                            <FileText size={14} className="mr-1.5"/>
                                                            Description
                                                        </h4>
                                                        <p className="text-gray-700 whitespace-pre-line border border-gray-100 rounded-md p-3 bg-gray-50">{request.description}</p>
                                                    </div>
                                                )}

                                                {request.admin_note && (
                                                    <div className="mt-4">
                                                        <h4 className="text-sm font-medium text-gray-500 mb-1 flex items-center">
                                                            <FileText size={14} className="mr-1.5"/>
                                                            Admin Note
                                                        </h4>
                                                        <p className="text-gray-700 whitespace-pre-line border border-gray-100 rounded-md p-3 bg-gray-50">{request.admin_note}</p>
                                                    </div>
                                                )}

                                                {request.status === 'pending' && (
                                                    <div className="mt-6 flex flex-wrap gap-3">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedRequest(request.id);
                                                            }}
                                                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                        >
                                                            Update Status
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center">
                                        <button
                                            className="p-2 text-gray-400 hover:text-gray-600"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleExpandRequest(request.id);
                                            }}
                                        >
                                            {expandedRequest === request.id ?
                                                <ChevronUp size={20}/> :
                                                <ChevronDown size={20}/>
                                            }
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                        {searchTerm || statusFilter ? (
                            <div className="flex flex-col items-center">
                                <Search size={40} className="text-gray-400 mb-4"/>
                                <h3 className="text-lg font-medium text-gray-800 mb-2">No requests found</h3>
                                <p className="text-gray-600 mb-4">
                                    No research requests match your current search filters
                                </p>
                                <button
                                    onClick={() => {
                                        setSearchTerm('');
                                        setStatusFilter('');
                                    }}
                                    className="text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                    Clear all filters
                                </button>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center">
                                <ClipboardList size={40} className="text-gray-400 mb-4"/>
                                <h3 className="text-lg font-medium text-gray-800 mb-2">No research requests
                                    available</h3>
                                <p className="text-gray-600">
                                    There are no researcher applications to display
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // Render grid view
    const renderGridView = () => {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredRequests.length > 0 ? (
                    filteredRequests.map((request) => (
                        <div
                            key={request.id}
                            className={`bg-white rounded-xl shadow-sm overflow-hidden border-t-4 ${
                                request.status === 'pending' ? 'border-amber-400' :
                                    request.status === 'approved' ? 'border-emerald-400' : 'border-red-400'
                            } transition-all duration-200 hover:shadow-md cursor-pointer`}
                            onClick={() => toggleExpandRequest(request.id)}
                        >
                            <div className="p-5">
                                <div className="flex justify-between items-start mb-3">
                                    <div
                                        className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                        <User size={20} className="text-blue-600"/>
                                    </div>

                                    <span
                                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColors(request.status).bg} ${getStatusColors(request.status).text}`}>
                    {getStatusColors(request.status).icon}
                                        <span className="ml-1 capitalize">{request.status}</span>
                  </span>
                                </div>

                                <h3 className="text-lg font-medium text-gray-900 mb-1 line-clamp-1">{request.user_name}</h3>
                                <p className="text-sm text-gray-500 mb-2 flex items-center">
                                    <Mail size={12} className="mr-1"/>
                                    <span className="truncate">{request.user_email}</span>
                                </p>

                                <div className="mb-3">
                                    <div
                                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-blue-50 text-blue-700">
                                        <BookOpen size={12} className="mr-1"/>
                                        <span className="truncate max-w-[180px]">{request.research_topic}</span>
                                    </div>
                                </div>

                                <p className="text-xs text-gray-500 flex items-center mt-3">
                                    <Calendar size={12} className="mr-1"/>
                                    {formatDate(request.created_at)}
                                </p>

                                {expandedRequest === request.id && (
                                    <div className="mt-4 border-t border-gray-100 pt-4">
                                        <div className="space-y-3">
                                            <div>
                                                <h4 className="text-xs font-medium text-gray-500 flex items-center">
                                                    <FileText size={12} className="mr-1"/>
                                                    Use Case
                                                </h4>
                                                <p className="mt-1 text-sm text-gray-700">{request.use_case}</p>
                                            </div>

                                            {request.organization && (
                                                <div>
                                                    <h4 className="text-xs font-medium text-gray-500 flex items-center">
                                                        <Building size={12} className="mr-1"/>
                                                        Organization
                                                    </h4>
                                                    <p className="mt-1 text-sm text-gray-700">{request.organization}</p>
                                                </div>
                                            )}

                                            {request.description && (
                                                <div>
                                                    <h4 className="text-xs font-medium text-gray-500 flex items-center">
                                                        <FileText size={12} className="mr-1"/>
                                                        Description
                                                    </h4>
                                                    <p className="mt-1 text-sm text-gray-700 line-clamp-3">{request.description}</p>
                                                </div>
                                            )}
                                        </div>

                                        {request.status === 'pending' && (
                                            <div className="mt-4">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedRequest(request.id);
                                                    }}
                                                    className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                >
                                                    Update Status
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="col-span-full bg-white rounded-xl shadow-sm p-8 text-center">
                        {searchTerm || statusFilter ? (
                            <div className="flex flex-col items-center">
                                <Search size={40} className="text-gray-400 mb-4"/>
                                <h3 className="text-lg font-medium text-gray-800 mb-2">No requests found</h3>
                                <p className="text-gray-600 mb-4">
                                    No research requests match your current search filters
                                </p>
                                <button
                                    onClick={() => {
                                        setSearchTerm('');
                                        setStatusFilter('');
                                    }}
                                    className="text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                    Clear all filters
                                </button>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center">
                                <ClipboardList size={40} className="text-gray-400 mb-4"/>
                                <h3 className="text-lg font-medium text-gray-800 mb-2">No research requests
                                    available</h3>
                                <p className="text-gray-600">
                                    There are no researcher applications to display
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // Get the selected request object for the modal
    const selectedRequestObj = selectedRequest ? requests.find(r => r.id === selectedRequest) : null;

    return (
        <div className="space-y-8">
            {/* Header with subtle blue gradient background */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 shadow-sm">
                <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                    <ClipboardList className="text-blue-600" size={32}/>
                    Researcher Requests
                </h1>
                <p className="mt-2 text-gray-600">
                    Manage and review applications for researcher access
                </p>
            </div>

            {/* Controls with glass effect */}
            <div className="bg-white bg-opacity-80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex flex-col lg:flex-row gap-5 items-start lg:items-center justify-between">
                    <div className="w-full lg:w-auto flex flex-col sm:flex-row gap-4 flex-wrap">
                        <div className="flex items-center gap-2">
                            <label htmlFor="page" className="text-sm font-medium text-gray-700">
                                Page:
                            </label>
                            <input
                                id="page"
                                type="number"
                                min="1"
                                value={page}
                                onChange={(e) => setPage(Math.max(1, Number(e.target.value)))}
                                className="w-16 border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        <div className="flex items-center gap-2">
                            <label htmlFor="pageSize" className="text-sm font-medium text-gray-700">
                                Results:
                            </label>
                            <select
                                id="pageSize"
                                value={pageSize}
                                onChange={(e) => setPageSize(Number(e.target.value))}
                                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                {[10, 25, 50, 100].map(size => (
                                    <option key={size} value={size}>{size}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex items-center gap-2">
                            <label htmlFor="statusFilter" className="text-sm font-medium text-gray-700">
                                Status:
                            </label>
                            <select
                                id="statusFilter"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="">All Statuses</option>
                                <option value="pending">Pending</option>
                                <option value="approved">Approved</option>
                                <option value="rejected">Rejected</option>
                            </select>
                        </div>

                        <div className="relative w-full sm:w-64">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Search size={16} className="text-gray-400"/>
                            </div>
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search requests..."
                                className="pl-10 w-full border border-gray-300 rounded-md py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-3 w-full lg:w-auto">
                        <div className="flex items-center bg-gray-100 rounded-md p-1">
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                title="List view"
                            >
                                <LayoutList size={18}/>
                            </button>
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                title="Grid view"
                            >
                                <LayoutGrid size={18}/>
                            </button>
                        </div>

                        <button
                            onClick={fetchRequests}
                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                        >
                            <RefreshCw size={16} className="mr-2"/>
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            {/* Requests List */}
            {isLoading ? (
                <div className="flex justify-center items-center h-64">
                    <div className="flex flex-col items-center">
                        <Loader2 size={40} className="animate-spin text-blue-600 mb-4"/>
                        <p className="text-gray-500">Loading requests...</p>
                    </div>
                </div>
            ) : error ? (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                    <div className="flex items-center">
                        <AlertTriangle size={24} className="text-red-500 mr-3"/>
                        <div>
                            <h3 className="text-red-800 font-medium">Error loading requests</h3>
                            <p className="text-red-700">{error}</p>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    {viewMode === 'list' ? renderListView() : renderGridView()}

                    {/* Pagination */}
                    {filteredRequests.length > 0 && (
                        <div className="flex items-center justify-between bg-white px-6 py-3 rounded-xl shadow-sm">
                            <div className="flex-1 flex justify-between sm:hidden">
                                <button
                                    onClick={() => setPage(Math.max(1, page - 1))}
                                    disabled={page === 1}
                                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => setPage(page + 1)}
                                    disabled={filteredRequests.length < pageSize}
                                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-sm text-gray-700">
                                        Showing <span className="font-medium">{filteredRequests.length}</span> results
                                        {pageSize < filteredRequests.length && (
                                            <> (limited to <span className="font-medium">{pageSize}</span> per page)</>
                                        )}
                                    </p>
                                </div>
                                <div>
                                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                                         aria-label="Pagination">
                                        <button
                                            onClick={() => setPage(Math.max(1, page - 1))}
                                            disabled={page === 1}
                                            className="relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <span className="sr-only">Previous</span>
                                            <ChevronLeft className="h-5 w-5" aria-hidden="true"/>
                                        </button>
                                        <span
                                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                      Page {page}
                    </span>
                                        <button
                                            onClick={() => setPage(page + 1)}
                                            disabled={filteredRequests.length < pageSize}
                                            className="relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <span className="sr-only">Next</span>
                                            <ChevronRight className="h-5 w-5" aria-hidden="true"/>
                                        </button>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Status update modal */}
            <StatusChangeModal
                request={selectedRequestObj}
                adminNote={adminNote}
                setAdminNote={setAdminNote}
                isUpdating={isUpdating}
                onClose={handleCloseModal}
                onUpdateStatus={updateRequestStatus}
                getStatusColors={getStatusColors}
            />
        </div>
    );
};

export default ResearchRequestsPage;